import {useEffect, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {useTranslation} from 'react-i18next';
import {Skeleton} from 'antd';
import {AgentSenderInput, QuickActionChipButton} from '../../AgentChatShared';
import {agentActions} from '../../../state/actions/agent.actions';
import {quickActionHelpers} from '../../../utils/helpers';
import {agentWebsocketFlowConstants} from '../../../constants';
import IconWrapper from '../../IconWrapper';
import {ChevronRightIcon} from '../../../icons';
import {
  StyledFullWidthAgentChatEmptyStateContainer,
  StyledFullWidthAgentChatEmptyStateTitle,
  StyledFullWidthAgentChatEmptyStateContentWrapper,
  StyledFullWidthAgentChatEmptyStateActionsRow,
  StyledFullWidthAgentChatEmptyStateOptionsLabel,
  StyledFullWidthAgentChatEmptyStateOptionsGrid,
  StyledFullWidthAgentChatEmptyStateOptionCard,
  StyledFullWidthAgentChatEmptyStateDescription,
} from './StyledFullWidthAgentChatEmptyState';

const flow = agentWebsocketFlowConstants.EXPENSE_ANALYSIS;
const SKELETON_GROUPS_COUNT = 4;
const SKELETON_OPTIONS_COUNT = 4;

const FullWidthAgentChatEmptyState = ({
  value,
  onChange,
  onSubmit,
  disabled,
  fileInputRef,
  onFileChange,
  onPlusClick,
  attachmentDrafts,
  onRemoveAttachment,
  onAttachmentsChange,
  onSelectQuickAction,
}) => {
  const [t] = useTranslation(['agentChat']);
  const dispatch = useDispatch();
  const [selectedGroupName, setSelectedGroupName] = useState(null);
  const [isQuickActionsLoading, setIsQuickActionsLoading] = useState(true);

  const quickActions = useSelector(state => state.agent.flowGreetingMessages[flow]?.quickActions) || [];
  const quickActionGroups = quickActionHelpers.groupQuickActions(quickActions);
  const activeGroupName = selectedGroupName ?? quickActionGroups[0]?.group ?? null;
  const activeGroup = quickActionGroups.find((group) => group.group === activeGroupName);

  useEffect(() => {
    dispatch(agentActions.getFlowGreetingMessage({
      flow,
      successCallback: () => setIsQuickActionsLoading(false),
      errorCallback: () => setIsQuickActionsLoading(false),
    }));
  }, [dispatch]);

  return (
    <StyledFullWidthAgentChatEmptyStateContainer>
      <StyledFullWidthAgentChatEmptyStateTitle>{t('agentChat:quickAction.title')}</StyledFullWidthAgentChatEmptyStateTitle>
      <StyledFullWidthAgentChatEmptyStateContentWrapper>
        <AgentSenderInput
          fileInputRef={fileInputRef}
          value={value}
          onChange={onChange}
          disabled={disabled}
          placeholder={t('agentChat:quickAction.placeholder')}
          onSubmit={onSubmit}
          onFileChange={onFileChange}
          onPlusClick={onPlusClick}
          attachmentDrafts={attachmentDrafts}
          onRemoveAttachment={onRemoveAttachment}
          onAttachmentsChange={onAttachmentsChange}
        />
      </StyledFullWidthAgentChatEmptyStateContentWrapper>
      {isQuickActionsLoading ? (
        <>
          <StyledFullWidthAgentChatEmptyStateActionsRow wrap size='small'>
            {Array.from({length: SKELETON_GROUPS_COUNT}).map((_, index) => (
              <Skeleton.Button key={index} active size='small' shape='round' style={{width: 96}} />
            ))}
          </StyledFullWidthAgentChatEmptyStateActionsRow>
          <StyledFullWidthAgentChatEmptyStateOptionsLabel>
            <Skeleton.Input active size='small' style={{width: 120}} />
          </StyledFullWidthAgentChatEmptyStateOptionsLabel>
          <StyledFullWidthAgentChatEmptyStateOptionsGrid>
            {Array.from({length: SKELETON_OPTIONS_COUNT}).map((_, index) => (
              <Skeleton.Input key={index} active block style={{height: 52, borderRadius: 12}} />
            ))}
          </StyledFullWidthAgentChatEmptyStateOptionsGrid>
          <StyledFullWidthAgentChatEmptyStateDescription>
            <Skeleton.Input active size='small' style={{width: 240}} />
          </StyledFullWidthAgentChatEmptyStateDescription>
        </>
      ) : activeGroup && (
        <>
          <StyledFullWidthAgentChatEmptyStateActionsRow wrap size='small'>
            {quickActionGroups.map((group) => (
              <QuickActionChipButton
                key={group.group}
                type='default'
                $isActive={group.group === activeGroupName}
                onClick={() => setSelectedGroupName(group.group)}
              >
                {group.group}
              </QuickActionChipButton>
            ))}
          </StyledFullWidthAgentChatEmptyStateActionsRow>
          <StyledFullWidthAgentChatEmptyStateOptionsLabel>{activeGroup.group}</StyledFullWidthAgentChatEmptyStateOptionsLabel>
          <StyledFullWidthAgentChatEmptyStateOptionsGrid>
            {activeGroup.items.map((item) => (
              <StyledFullWidthAgentChatEmptyStateOptionCard
                key={item.label}
                type='text'
                onClick={() => onSelectQuickAction(item)}
              >
                <span>{item.label}</span>
                <IconWrapper size={16} ghost><ChevronRightIcon /></IconWrapper>
              </StyledFullWidthAgentChatEmptyStateOptionCard>
            ))}
          </StyledFullWidthAgentChatEmptyStateOptionsGrid>
          <StyledFullWidthAgentChatEmptyStateDescription>
            {t('agentChat:fullWidth.emptyState.description')}
          </StyledFullWidthAgentChatEmptyStateDescription>
        </>
      )}
    </StyledFullWidthAgentChatEmptyStateContainer>
  );
};

export default FullWidthAgentChatEmptyState;
